// Home page motion — every animation on the home page lives here.
//
// Sections, in order:
//   1. FOUC guard (top-level IIFE — runs at module-import time)
//   2. acquireEntrance()    — Acquire section's full choreography on scroll
//   3. sectionReveals()     — declarative fadeUp / staggerList / opacityFade
//                              for the rest of the home page sections
//
// The hero entrance lives in ./cinematic-hero.js (the old scale-based
// heroEntrance was removed). home/index.js runs it directly.
//
// Custom JS animations that are NOT motion (kept as separate files):
//   - hp-challenge.js          — sticky 3-card scroll-driven stack
//   - hp-acquire-card.js       — Acquire card click-to-expand interaction
//   - companies-marquee.js     — mobile companies marquee
//   - number-anim.js           — impact stat counter
//   - video-hero.js            — hero video play/pause
//   - swiper-cs.js             — case-studies Swiper init

import {
  ease as resolveEase,
  VOY_EASE,
  VOY_OUT_QUART,
  VOY_CUSTOM,
  registerEasings as registerProjectEasings,
  fadeUp,
  staggerList,
  opacityFade,
  scrollTriggerConfig,
  prefersReducedMotion,
} from '../../motion';

// ─────────────────────────────────────────────────────────────────────────────
// FOUC guard — runs at module-import time, BEFORE first paint.
//
// The hero's title group, paragraph, and CTAs are animated in via opacity 0→1.
// Without this guard, browsers can paint the elements at full opacity for one
// frame before GSAP's gsap.set() takes hold, causing a visible flicker.
// Injecting a <style> tag synchronously hides them until JS sets transforms.
//
// For 100% reliability across all loading conditions, also paste this snippet
// into Webflow's page-head custom code (Page Settings → Custom Code → head):
//
//   <style>
//     .section_hp-hero .hp-hero_header-warp,
//     .section_hp-hero .hp-hero_p2,
//     .section_hp-hero .main-cta,
//     .section_hp-hero .button.is--secondary {
//       opacity: 0;
//     }
//   </style>
// ─────────────────────────────────────────────────────────────────────────────
(function injectHeroFoucGuard() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('hp-hero-fouc-guard')) return;
  const style = document.createElement('style');
  style.id = 'hp-hero-fouc-guard';
  style.textContent = `
    .section_hp-hero .hp-hero_header-warp,
    .section_hp-hero .hp-hero_p2,
    .section_hp-hero .hp-hero_buttons-wrap .main-cta,
    .section_hp-hero .hp-hero_buttons-wrap .button.is--secondary {
      opacity: 0;
    }
  `;
  (document.head || document.documentElement).appendChild(style);
})();

// ─────────────────────────────────────────────────────────────────────────────
// 2. ACQUIRE ENTRANCE
// Triggered when the .section_hp-acquire enters the viewport. Plays once,
// then leaves the .acquire-arrows_svg rotating slowly forever via CSS keyframes.
//
// Custom ease "voyCustom" (cubic-bezier 0.5,0,0,1) is used for elastic-feeling
// transforms on the center wheel and arrows. Registered locally — not part of
// the shared project ease registry since only this section uses it.
// ─────────────────────────────────────────────────────────────────────────────
const ACQUIRE_SELECTORS = {
  section: '.section_hp-acquire',
  // Outer header (per Figma annotations: subtitle / title / description)
  subtitle: '.hp-acquire_header .hp_label',
  title: '.hp-acquire_h2',
  description: '.hp-acquire_p',
  // The dark visual frame that wraps the cloud/wheel/arrows
  container: '.hp-acquire_animated-div',
  // 4 bottom cards: Predict / Signal Engineer / Activate / Learn & Evolve
  bottomCards: '.acuire-card',
  // Existing inner choreography
  cloud: '.cloud',
  mobileCloud: '.mobile-cloud',
  dataCards: '.hp-acquire_top-left-flex .hp-acquire_top-left-card',
  outerArrows: '.acquire-arrows_svg',
  centerActiveImage: '.hp-acquire_top-center-image.is--active',
  leftArrow: '.hp-acquire_top-left .arrow',
  rightArrow: '.hp-acquire_top-right .arrow.is--right',
  rightWrapper: '.hp-acquire_top-right-wrapper',
  rightItems: '.hp-acquire_top-right-item',
};

const ACQUIRE_SCROLL_START = 'top 65%';

function registerAcquireEasings() {
  registerProjectEasings();
  return {
    outQuart: resolveEase(VOY_OUT_QUART),
    voyCustom: resolveEase(VOY_CUSTOM),
  };
}

function injectAcquireRotationStyles() {
  if (document.getElementById('hp-acquire-arrows-styles')) return;
  const style = document.createElement('style');
  style.id = 'hp-acquire-arrows-styles';
  style.textContent = `
    @keyframes hp-acquire-arrows-spin { to { rotate: 360deg; } }
    .acquire-arrows_svg.is-spinning {
      animation: hp-acquire-arrows-spin 10s linear infinite;
      will-change: transform;
    }
  `;
  document.head.appendChild(style);
}

function acquireEntrance() {
  const section = document.querySelector(ACQUIRE_SELECTORS.section);
  if (!section) return;

  gsap.registerPlugin(ScrollTrigger);
  const ease = registerAcquireEasings();
  injectAcquireRotationStyles();

  // Outer header elements (per Figma annotations)
  const subtitle = section.querySelector(ACQUIRE_SELECTORS.subtitle);
  const title = section.querySelector(ACQUIRE_SELECTORS.title);
  const description = section.querySelector(ACQUIRE_SELECTORS.description);
  const container = section.querySelector(ACQUIRE_SELECTORS.container);
  const bottomCards = section.querySelectorAll(ACQUIRE_SELECTORS.bottomCards);

  // Inner choreography
  const cloud = section.querySelector(ACQUIRE_SELECTORS.cloud);
  const mobileCloud = section.querySelector(ACQUIRE_SELECTORS.mobileCloud);
  const dataCards = section.querySelectorAll(ACQUIRE_SELECTORS.dataCards);
  const outerArrows = section.querySelector(ACQUIRE_SELECTORS.outerArrows);
  const centerImage = section.querySelector(ACQUIRE_SELECTORS.centerActiveImage);
  const leftArrow = section.querySelector(ACQUIRE_SELECTORS.leftArrow);
  const rightArrow = section.querySelector(ACQUIRE_SELECTORS.rightArrow);
  const rightWrapper = section.querySelector(ACQUIRE_SELECTORS.rightWrapper);
  const rightItems = section.querySelectorAll(ACQUIRE_SELECTORS.rightItems);

  const mm = gsap.matchMedia();

  mm.add(
    {
      isDesktop: '(min-width: 768px)',
      isMobile: '(max-width: 767px)',
      reduce: '(prefers-reduced-motion: reduce)',
    },
    (context) => {
      const { isDesktop, isMobile, reduce } = context.conditions;
      const activeCloud = isDesktop ? cloud : mobileCloud;

      const initialStates = [];

      // ─── Outer header initial states (Figma annotations) ──────────
      if (subtitle) {
        gsap.set(subtitle, { opacity: 0, y: 50, willChange: 'transform, opacity' });
        initialStates.push(subtitle);
      }
      if (title) {
        gsap.set(title, { opacity: 0, y: 50, willChange: 'transform, opacity' });
        initialStates.push(title);
      }
      if (description) {
        gsap.set(description, { opacity: 0, y: 50, willChange: 'transform, opacity' });
        initialStates.push(description);
      }
      if (container) {
        // Container scales 0.7 → 1; transforms compose with children's own
        // animations (cloud, wheel, arrows) so everything coordinates.
        gsap.set(container, { scale: 0.7, transformOrigin: '50% 50%', willChange: 'transform' });
        initialStates.push(container);
      }
      if (bottomCards.length) {
        gsap.set(bottomCards, { opacity: 0, y: 50, willChange: 'transform, opacity' });
        initialStates.push(...bottomCards);
      }

      // ─── Inner choreography initial states ────────────────────────
      if (activeCloud) {
        gsap.set(activeCloud, { opacity: 0, y: -20, willChange: 'transform, opacity' });
        initialStates.push(activeCloud);
      }
      if (dataCards.length) {
        gsap.set(dataCards, { opacity: 0, y: 40, willChange: 'transform, opacity' });
        initialStates.push(...dataCards);
      }
      if (leftArrow) {
        gsap.set(leftArrow, { clipPath: 'inset(0 100% 0 0)', opacity: 1 });
        initialStates.push(leftArrow);
      }
      if (rightArrow) {
        gsap.set(rightArrow, { clipPath: 'inset(0 100% 0 0)', opacity: 1 });
        initialStates.push(rightArrow);
      }
      if (outerArrows) {
        gsap.set(outerArrows, {
          opacity: 0,
          scale: 0,
          rotation: -270,
          transformOrigin: '50% 50%',
          willChange: 'transform, opacity',
        });
        initialStates.push(outerArrows);
      }
      if (centerImage) {
        gsap.set(centerImage, {
          opacity: 0,
          scale: 0,
          rotation: 45,
          transformOrigin: '50% 50%',
          willChange: 'transform, opacity',
        });
        initialStates.push(centerImage);
      }
      if (rightWrapper) {
        gsap.set(rightWrapper, { opacity: 0, x: -30, willChange: 'transform, opacity' });
        initialStates.push(rightWrapper);
      }
      if (rightItems.length) {
        gsap.set(rightItems, {
          opacity: 0,
          y: 10,
          scale: 1.1,
          transformOrigin: '50% 50%',
          willChange: 'transform, opacity',
        });
        initialStates.push(...rightItems);
      }

      // Reduced motion — jump to final state, no ScrollTrigger. The shared
      // gsap.set covers the new outer elements too (they only differ in
      // y / scale, which all settle to 0 / 1 here).
      if (reduce) {
        gsap.set(initialStates, {
          opacity: 1,
          x: 0,
          y: 0,
          scale: 1,
          rotation: 0,
          clipPath: 'inset(0 0% 0 0)',
          clearProps: 'willChange',
        });
        return;
      }

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: ACQUIRE_SCROLL_START,
          toggleActions: 'play none none none',
        },
        defaults: { overwrite: 'auto' },
        onComplete: () => {
          // Strip willChange except from the spinning arrows (kept by .is-spinning).
          gsap.set(
            initialStates.filter((el) => el !== outerArrows),
            { clearProps: 'willChange' }
          );
        },
      });

      // ─── Outer header tweens (Figma annotations) ──────────────────
      // Subtitle, title, and the 4 bottom cards: opacity 0→1 (1s linear)
      // + y 50→0 (1s voyOutQuart). Description: same with 0.2s delay.
      // Container: scale 0.7→1 (0.8s voyCustom).
      if (subtitle) {
        tl.to(subtitle, { opacity: 1, duration: 1, ease: 'none' }, 0);
        tl.to(subtitle, { y: 0, duration: 1, ease: ease.outQuart }, 0);
      }
      if (title) {
        tl.to(title, { opacity: 1, duration: 1, ease: 'none' }, 0);
        tl.to(title, { y: 0, duration: 1, ease: ease.outQuart }, 0);
      }
      if (description) {
        tl.to(description, { opacity: 1, duration: 1, ease: 'none' }, 0.2);
        tl.to(description, { y: 0, duration: 1, ease: ease.outQuart }, 0.2);
      }
      if (container) {
        tl.to(container, { scale: 1, duration: 0.8, ease: ease.voyCustom }, 0);
      }
      // 4 bottom cards stagger at 0.15s gap
      if (bottomCards.length) {
        bottomCards.forEach((card, i) => {
          const delay = i * 0.15;
          tl.to(card, { opacity: 1, duration: 1, ease: 'none' }, delay);
          tl.to(card, { y: 0, duration: 1, ease: ease.outQuart }, delay);
        });
      }

      // ─── Inner choreography tweens ────────────────────────────────
      if (activeCloud) {
        tl.to(activeCloud, { opacity: 1, duration: 0.25, ease: 'none' }, 0);
        tl.to(activeCloud, { y: 0, duration: 0.75, ease: ease.outQuart }, 0);
      }

      // Three data cards stagger at 0 / 0.10 / 0.15s.
      if (dataCards.length) {
        const cardDelays = [0, 0.1, 0.15];
        dataCards.forEach((card, i) => {
          const delay = cardDelays[i] ?? 0.15;
          tl.to(card, { opacity: 1, duration: 0.25, ease: 'none' }, delay);
          tl.to(card, { y: 0, duration: 0.7, ease: ease.outQuart }, delay);
        });
      }

      // Center wheel + outer arrows scale & rotate together at 0.15s.
      if (centerImage) {
        tl.to(
          centerImage,
          { opacity: 1, scale: 1, rotation: 0, duration: 1.25, ease: ease.voyCustom },
          0.15
        );
      }
      if (outerArrows) {
        tl.to(
          outerArrows,
          { scale: 1, rotation: 0, duration: 1.25, ease: ease.voyCustom },
          0.15
        );
        // 1.40s — hand off to CSS keyframe animation for the continuous spin
        // (zero per-frame JS cost, no visible pause for the rest of the timeline).
        tl.call(
          () => {
            gsap.set(outerArrows, { clearProps: 'transform' });
            outerArrows.classList.add('is-spinning');
          },
          null,
          1.4
        );
      }

      // Left arrow mask-reveal L→R at 0.20s (desktop only).
      if (leftArrow && isDesktop) {
        tl.to(
          leftArrow,
          { clipPath: 'inset(0 0% 0 0)', duration: 0.75, ease: ease.voyCustom },
          0.2
        );
      }

      // Outer arrows fade in at 0.70s (scale/rotate already in motion).
      if (outerArrows) {
        tl.to(outerArrows, { opacity: 1, duration: 0.3, ease: 'none' }, 0.7);
      }

      // Right arrow mask-reveal at 0.80s (desktop only).
      if (rightArrow && isDesktop) {
        tl.to(
          rightArrow,
          { clipPath: 'inset(0 0% 0 0)', duration: 0.75, ease: ease.voyCustom },
          0.8
        );
      }

      // Right wrapper slides in at 1.20s — horizontal on desktop, vertical on mobile.
      if (rightWrapper) {
        const slideProp = isDesktop ? { x: 0 } : { y: 0 };
        if (isMobile) gsap.set(rightWrapper, { x: 0, y: -30 });
        tl.to(rightWrapper, { opacity: 1, duration: 0.25, ease: 'none' }, 1.2);
        tl.to(rightWrapper, { ...slideProp, duration: 0.75, ease: ease.outQuart }, 1.2);
      }

      // Two right items fade up at 1.40s and 1.50s.
      if (rightItems.length) {
        const itemDelays = [1.4, 1.5];
        rightItems.forEach((item, i) => {
          const delay = itemDelays[i] ?? 1.5;
          tl.to(item, { opacity: 1, duration: 0.1, ease: 'none' }, delay);
          tl.to(item, { y: 0, duration: 0.4, ease: ease.outQuart }, delay);
        });
        // Items "settle" with scale 1.1 → 1 at 1.70s and 1.80s.
        const settleDelays = [1.7, 1.8];
        rightItems.forEach((item, i) => {
          const delay = settleDelays[i] ?? 1.8;
          tl.to(item, { scale: 1, duration: 0.5, ease: ease.voyCustom }, delay);
        });
      }

      return () => {
        tl.scrollTrigger?.kill();
        tl.kill();
        gsap.set(initialStates, { clearProps: 'all' });
        if (outerArrows) outerArrows.classList.remove('is-spinning');
      };
    }
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Challenge section entrance — the whole layout (header + cards) enters
// together. Per Figma:
//   • opacity 0 → 1 in 1s linear
//   • translateY +200 → 0 in 1s cubic-bezier(0,0,0,1) (voyOutQuart)
//   • no delay
//
// This animates the .hp-challenge_layout container, so all child cards move
// with it. Once the entrance finishes, hp-challenge.js takes over the
// per-card sticky/pin scroll choreography (those use card-level transforms
// that compose cleanly with the parent's settled transform).
// ─────────────────────────────────────────────────────────────────────────────
function challengeEntrance() {
  const layout = document.querySelector('.hp-challenge_layout');
  if (!layout) return;

  gsap.set(layout, { opacity: 0, y: 200, willChange: 'transform, opacity' });

  if (prefersReducedMotion()) {
    gsap.set(layout, { opacity: 1, y: 0, clearProps: 'willChange' });
    return;
  }

  const tl = gsap.timeline({
    scrollTrigger: scrollTriggerConfig({
      trigger: '.section_hp-challenge',
      once: true,
    }),
    onComplete: () => gsap.set(layout, { clearProps: 'willChange' }),
  });

  // Linear opacity per Figma
  tl.to(layout, { opacity: 1, duration: 1, ease: 'none' }, 0);
  // voyOutQuart on Y per Figma
  tl.to(layout, { y: 0, duration: 1, ease: resolveEase(VOY_OUT_QUART) }, 0);
}

// ─────────────────────────────────────────────────────────────────────────────
// Case Studies cards entrance — every swiper slide fades in and slides
// from x -150px. Per Figma:
//   • Opacity 0 → 1 in 1s linear
//   • translateX -150 → 0 in 1s cubic-bezier(0,0,0,1) (voyOutQuart)
//   • No delay (all cards animate together; no stagger spec'd)
//
// Targets every .swiper-slide.is--home (originals + Swiper's loop clones).
// Swiper transforms the wrapper, not individual slides, so our per-slide x
// animation composes cleanly with whatever wrapper translate Swiper applies.
// ─────────────────────────────────────────────────────────────────────────────
function caseStudiesCardsEntrance() {
  const slides = document.querySelectorAll(
    '.section_hp-case-studies .swiper-slide.is--home'
  );
  if (!slides.length) return;

  gsap.set(slides, { opacity: 0, x: -150, willChange: 'transform, opacity' });

  if (prefersReducedMotion()) {
    gsap.set(slides, { opacity: 1, x: 0, clearProps: 'willChange' });
    return;
  }

  const tl = gsap.timeline({
    scrollTrigger: scrollTriggerConfig({
      trigger: '.section_hp-case-studies',
      once: true,
    }),
    // Clear ALL inline transforms so Swiper can measure the slides cleanly
    // during loop teleports — a leftover identity matrix on slides confuses
    // Swiper's loop reset logic and produces a visible jump at the boundary.
    onComplete: () => gsap.set(slides, { clearProps: 'all' }),
  });

  // Linear opacity per Figma
  tl.to(slides, { opacity: 1, duration: 1, ease: 'none' }, 0);
  // voyOutQuart on X per Figma
  tl.to(slides, { x: 0, duration: 1, ease: resolveEase(VOY_OUT_QUART) }, 0);
}

// ─────────────────────────────────────────────────────────────────────────────
// Companies logo stagger — Figma spec'd separate eases per property
// (linear opacity + voyOutQuart on translateX), so it can't be expressed
// with a single fadeUp call. Two parallel tweens with the same stagger.
// ─────────────────────────────────────────────────────────────────────────────
function companiesLogoStagger() {
  const wrappers = document.querySelectorAll(
    '.section_hero-companies .companies_logo-wrapper'
  );
  if (!wrappers.length) return;

  // Initial state — set on every wrapper (originals + mobile marquee clones).
  gsap.set(wrappers, { opacity: 0, x: -25, willChange: 'transform, opacity' });

  if (prefersReducedMotion()) {
    gsap.set(wrappers, { opacity: 1, x: 0, clearProps: 'willChange' });
    return;
  }

  const tl = gsap.timeline({
    scrollTrigger: scrollTriggerConfig({
      trigger: '.section_hero-companies',
      once: true,
    }),
    onComplete: () => gsap.set(wrappers, { clearProps: 'willChange' }),
  });

  // Cascade tuning. We want the same TOTAL cascade window (~1.4s) regardless
  // of how many wrappers exist. On desktop there are ~16 wrappers, on mobile
  // companies-marquee.js doubles them with clones (~32). A fixed stagger
  // doubles the mobile cascade duration, which felt too long. Computing
  // stagger from the count keeps the perceived rhythm identical:
  //   total cascade = duration + count × stagger
  //   16 wrappers → 0.6 + 16×0.05 = 1.4s
  //   32 wrappers → 0.6 + 32×0.025 = 1.4s
  // Capped at 0.05 so very small counts don't blow the gap out.
  const PER_LOGO_DURATION = 0.6;
  const STAGGER = Math.min(0.05, 0.8 / wrappers.length);

  // Opacity fade — linear ease per Figma
  tl.to(
    wrappers,
    { opacity: 1, duration: PER_LOGO_DURATION, ease: 'none', stagger: STAGGER },
    0
  );
  // Slide in from -25px — voyOutQuart per Figma
  tl.to(
    wrappers,
    {
      x: 0,
      duration: PER_LOGO_DURATION,
      ease: resolveEase(VOY_OUT_QUART),
      stagger: STAGGER,
    },
    0
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Live integrations logo stagger — same pattern as the companies cascade
// at the top of the page (linear opacity + voyOutQuart on translateX),
// applied to the third party logos in the "No new system to learn" section.
//
// The section has THREE rows. Each row has two holders: a visible one and
// an .is--mobile-hidden duplicate that's only revealed on mobile for the
// marquee. On desktop we exclude the duplicates so the stagger only cascades
// through the logos the user can actually see; on mobile we include them so
// the duplicates also fade in cleanly before the marquee picks them up.
// ─────────────────────────────────────────────────────────────────────────────
function liveIntegrationsLogosStagger() {
  const isMobile = window.innerWidth < 992;
  const selector = isMobile
    ? '.section_hp-live .hp-live_logo-wrapper'
    : '.section_hp-live .hp-live_logos-holder:not(.is--mobile-hidden) .hp-live_logo-wrapper';
  const wrappers = document.querySelectorAll(selector);
  if (!wrappers.length) return;

  gsap.set(wrappers, { opacity: 0, x: -25, willChange: 'transform, opacity' });

  if (prefersReducedMotion()) {
    gsap.set(wrappers, { opacity: 1, x: 0, clearProps: 'willChange' });
    return;
  }

  const tl = gsap.timeline({
    scrollTrigger: scrollTriggerConfig({
      trigger: '.section_hp-live',
      once: true,
    }),
    onComplete: () => gsap.set(wrappers, { clearProps: 'willChange' }),
  });

  // Same cadence as the companies cascade — total window stays ~1.4s
  // regardless of count.
  const PER_LOGO_DURATION = 0.6;
  const STAGGER = Math.min(0.05, 0.8 / wrappers.length);

  tl.to(
    wrappers,
    { opacity: 1, duration: PER_LOGO_DURATION, ease: 'none', stagger: STAGGER },
    0
  );
  tl.to(
    wrappers,
    {
      x: 0,
      duration: PER_LOGO_DURATION,
      ease: resolveEase(VOY_OUT_QUART),
      stagger: STAGGER,
    },
    0
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Generic "scale entrance" — used for any visual that should scale up from
// some smaller starting size. 0.8s with voyCustom ease (cubic-bezier
// 0.5,0,0,1) per Figma. Pass a custom `fromScale` for elements that need a
// different starting point (e.g. CTA block is 0.5 vs lottie containers at 0.7).
// ─────────────────────────────────────────────────────────────────────────────
function mediaScaleEntrance(selector, fromScale = 0.7) {
  const el = document.querySelector(selector);
  if (!el) return;

  gsap.set(el, { scale: fromScale, transformOrigin: '50% 50%', willChange: 'transform' });

  if (prefersReducedMotion()) {
    gsap.set(el, { scale: 1, clearProps: 'willChange' });
    return;
  }

  gsap.to(el, {
    scale: 1,
    duration: 0.8,
    ease: resolveEase(VOY_CUSTOM),
    scrollTrigger: scrollTriggerConfig({ trigger: el, once: true }),
    onComplete: () => gsap.set(el, { clearProps: 'willChange' }),
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Global CTA entrance — animates the whole .section_global-cta as one unit.
// Targeting inner wrappers (.cta-wrapper, .global-cta-trigger-wrap, the
// white card) caused the card to stay hidden, so we animate the section
// itself. gsap.from keeps the section visible if the trigger never fires.
// ─────────────────────────────────────────────────────────────────────────────
function ctaEntrance() {
  const section = document.querySelector('.section_global-cta');
  if (!section) return;

  if (prefersReducedMotion()) return;

  gsap.from(section, {
    opacity: 0,
    y: 40,
    duration: 1,
    ease: resolveEase(VOY_EASE),
    scrollTrigger: scrollTriggerConfig({
      trigger: section,
      start: 'top bottom',
      once: true,
    }),
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Why-section comparison table entrance — per Figma:
//   • opacity 0 → 1 in 1s linear
//   • translateY +100 → 0 in 1s voyOutQuart
// Two parallel tweens (different eases per property).
// ─────────────────────────────────────────────────────────────────────────────
function whyTableEntrance() {
  const table = document.querySelector('.hp-why_table');
  if (!table) return;

  gsap.set(table, { opacity: 0, y: 100, willChange: 'transform, opacity' });

  if (prefersReducedMotion()) {
    gsap.set(table, { opacity: 1, y: 0, clearProps: 'willChange' });
    return;
  }

  const tl = gsap.timeline({
    scrollTrigger: scrollTriggerConfig({ trigger: '.hp-why_table', once: true }),
    onComplete: () => gsap.set(table, { clearProps: 'willChange' }),
  });
  tl.to(table, { opacity: 1, duration: 1, ease: 'none' }, 0);
  tl.to(table, { y: 0, duration: 1, ease: resolveEase(VOY_OUT_QUART) }, 0);
}

// ─────────────────────────────────────────────────────────────────────────────
// Optimization 3-card platform section — responsive behavior:
//   Desktop (≥992px): single timeline triggered when section enters view.
//     Cards reveal RIGHT to LEFT — TikTok first (no delay), Meta second
//     (0.15s), Google Ads third (0.3s). All slide in from x: -180.
//   Mobile (<992px): each card has its own ScrollTrigger so they animate
//     individually as they enter view (cards stack vertically on mobile,
//     so the reverse desktop order would have shown bottom cards before
//     the top ones — bad UX).
// ─────────────────────────────────────────────────────────────────────────────
function optimizationCardsEntrance() {
  const cards = document.querySelectorAll(
    '.section_hp-optimization .hp-optimization_card'
  );
  if (!cards.length) return;

  gsap.set(cards, { opacity: 0, x: -180, willChange: 'transform, opacity' });

  if (prefersReducedMotion()) {
    gsap.set(cards, { opacity: 1, x: 0, clearProps: 'willChange' });
    return;
  }

  const mm = gsap.matchMedia();

  // Desktop: reverse-order cascade triggered by section
  mm.add('(min-width: 992px)', () => {
    const tl = gsap.timeline({
      scrollTrigger: scrollTriggerConfig({
        trigger: '.section_hp-optimization',
        once: true,
      }),
      onComplete: () => gsap.set(cards, { clearProps: 'willChange' }),
    });

    // Reverse iteration: last card (TikTok) enters first
    const reversed = Array.from(cards).reverse();
    reversed.forEach((card, i) => {
      const delay = i * 0.15;
      tl.to(card, { opacity: 1, duration: 1, ease: 'none' }, delay);
      tl.to(card, { x: 0, duration: 1, ease: resolveEase(VOY_OUT_QUART) }, delay);
    });

    return () => tl.kill();
  });

  // Mobile: each card has its own trigger (individual scroll reveals)
  mm.add('(max-width: 991px)', () => {
    const tweens = [];
    cards.forEach((card) => {
      const tl = gsap.timeline({
        scrollTrigger: scrollTriggerConfig({ trigger: card, once: true }),
        onComplete: () => gsap.set(card, { clearProps: 'willChange' }),
      });
      tl.to(card, { opacity: 1, duration: 1, ease: 'none' }, 0);
      tl.to(card, { x: 0, duration: 1, ease: resolveEase(VOY_OUT_QUART) }, 0);
      tweens.push(tl);
    });
    return () => tweens.forEach((t) => t.kill());
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. SECTION REVEALS
// Declarative scroll reveals for sections that don't have a custom timeline.
// All triggers fire at "top bottom-=100" (Figma global rule) and play once.
// ─────────────────────────────────────────────────────────────────────────────
function sectionReveals() {
  // ─── Challenge section — full layout entrance ──────────────────
  challengeEntrance();

  // ─── Companies — staggered logo cascade ────────────────────────
  // Per Figma: each .companies_logo-wrapper fades + slides in from -25px.
  //   • Opacity 0 → 1 in 0.3s linear
  //   • translateX -25px → 0 in 0.3s with cubic-bezier(0,0,0,1) (voyOutQuart)
  //   • 0.015s stagger between logos
  //
  // Mobile: companies-marquee.js duplicates list children inline before this
  // runs (in home/index.js, marquee is initialized first), so the
  // querySelectorAll below picks up both originals AND clones — every visible
  // logo gets the cascade. The marquee animates xPercent on the parent list,
  // not on individual wrappers, so our per-wrapper x animation composes
  // cleanly with the marquee transform.
  // (No fadeUp on .hp-companies_h2 — Figma annotations don't call for one.)
  companiesLogoStagger();

  // ─── Why section header (.hp-acquire_h2 / _p classes are reused here) ─
  // The Acquire section's own header is animated as part of acquireEntrance
  // with its custom Figma-spec'd values — these scoped selectors only
  // reveal the Why section's reused classes.
  fadeUp('.section_hp-why .hp-acquire_h2');
  fadeUp('.section_hp-why .hp-acquire_p', { delay: 0.1 });

  // ─── Case Studies ───────────────────────────────────────
  fadeUp('.hp-case-studies_h2');
  // Each slide slides in from x: -150 with linear opacity (per Figma)
  caseStudiesCardsEntrance();

  // ─── Optimization (heading reused on Live integrations) ──────────
  fadeUp('.hp-optimization_h2');
  fadeUp('.hp-optimization_p', { delay: 0.1 });
  // Cards: reverse-stagger right→left on desktop (TikTok first), per-card
  // ScrollTriggers on mobile (cards stack vertically).
  optimizationCardsEntrance();

  // ─── Live integrations — staggered logo cascade ─────────────────
  // Same look + cadence as the companies cascade at the top of the page.
  liveIntegrationsLogosStagger();

  // ─── Decisioning ────────────────────────────────────────
  fadeUp('.hp-decisioning_h2');
  fadeUp('.hp-decisioning_p', { delay: 0.1 });
  staggerList('.hp-decisioning_tags-wrap > *', { gap: 0.04 });
  // Lottie visual: scale 70% → 100% per Figma
  mediaScaleEntrance('.hp-decisioning_media');

  // ─── Why Table ──────────────────────────────────────────
  // Per Figma: opacity linear + translateY +100 → 0 voyOutQuart, both 1s
  whyTableEntrance();
  staggerList('.hp-why_flex-numbers > *', { gap: 0.1 });

  // ─── Acquisition ────────────────────────────────────────
  fadeUp('.hp-acquisition_h2');
  fadeUp('.hp-acquisition_p', { delay: 0.1 });
  fadeUp('.hp-acquisiion_btn-wrap', { delay: 0.2 });
  // Lottie visual: scale 70% → 100% per Figma
  mediaScaleEntrance('.hp-acquisition_media');

  // ─── Global CTA ─────────────────────────────────────────
  // Animate the whole section as one unit. Targeting inner wrappers caused
  // the white card to stay hidden — animating the section itself avoids the
  // Webflow CSS variable / stacking context interaction inside.
  ctaEntrance();
}

// ─────────────────────────────────────────────────────────────────────────────
// Page motion entry points — called by home/index.js. The hero entrance now
// lives in ./cinematic-hero.js (the old heroEntrance was removed); the home page
// composes initCinematicHero + acquireEntrance + sectionReveals directly.
// ─────────────────────────────────────────────────────────────────────────────
export { acquireEntrance, sectionReveals };
